"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.patchInvoiceStatusRequestSchema = exports.saveInvoiceRequestSchema = exports.invoiceSummarySchema = exports.invoiceSchema = exports.INVOICE_TARGETS = exports.INVOICE_STATUSES = void 0;
exports.billedTotal = billedTotal;
exports.remainingToBill = remainingToBill;
exports.billedPct = billedPct;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Számlák — C57, Phase 4. What has been billed, and what is left to bill.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A VOID INVOICE COUNTS FOR NOTHING, AND IT IS THE ONLY STATUS THAT DOES.** Every sum in
 * this module filters `status !== 'void'` — the mockup does the same in four places, and the reason
 * it is worth stating is that *Piszkozat* does NOT. A draft is money we intend to ask for, so it is
 * billed as far as *Hátralévő* is concerned; a sztornó invoice is money nobody ever owed.
 *
 * ⚠️ **Two numbers, and neither replaces the other.** `number` is ours (`SZ-5011`); `externalId` is
 * the accounting system's (`LN-2026-0388`), and it is the one somebody reconciles against a bank
 * statement. The detail screen titles itself with the external one.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** `INV_ST` in the mockup, in its own order. */
exports.INVOICE_STATUSES = ['draft', 'issued', 'paid', 'void'];
/**
 * ⚠️ **Two arms, where an external cost has three.** Money billed OUT always has a deliverable
 * behind it — `PHASE-4.md`: *"All three anchor on a line item or a contract occurrence — never on a
 * project directly."*
 */
exports.INVOICE_TARGETS = ['line_item', 'occurrence'];
exports.invoiceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `inv_21b5a1e2` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('invoice'),
    /** Ours — `SZ-5011`. */
    number: zod_1.z.number().int(),
    /** Theirs — `LN-2026-0388`. */
    externalId: zod_1.z.string(),
    /**
     * 22 Oct 2026 · whether the caller may change it — `contract.manage`, at `own` only on a project
     * they are PM of (`project-manage.ts`). The screen offers no edit the server would refuse.
     */
    canManage: zod_1.z.boolean(),
    projectId: zod_1.z.string(),
    projectName: zod_1.z.string(),
    projectColor: zod_1.z.string().nullable(),
    targetKind: zod_1.z.enum(exports.INVOICE_TARGETS),
    lineItemId: zod_1.z.string().nullable(),
    lineItemName: zod_1.z.string().nullable(),
    /** ⚠️ Its ordinal within the contract, which is half of `/contracts/:code/:number`. */
    lineItemNumber: zod_1.z.number().int().nullable(),
    contractId: zod_1.z.string().nullable(),
    contractCode: zod_1.z.string().nullable(),
    /** R-57 · the line item's and contract's public identifiers — the address the screens link to. */
    lineItemIdentifier: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    occurrenceStartsOn: wireDateSchema.nullable(),
    /** ⚠️ The occurrence's ordinal, resolved server-side — `HSO-C01 #6` is what the screen prints,
        and the client does not hold the recurrence needed to count to six. Null on a line item. */
    occurrenceIndex: zod_1.z.number().int().nullable(),
    /** ⚠️ A string, like every other `numeric` on the wire. */
    amount: zod_1.z.string(),
    currencyCode: zod_1.z.string(),
    issuedOn: wireDateSchema,
    status: zod_1.z.enum(exports.INVOICE_STATUSES),
    /** The note as plain text — derived from `noteHtml` on every write (`0210`). For search and lists. */
    note: zod_1.z.string(),
    /**
     * ⚠️ `0210` · the note as SANITISED HTML, on the comments' allow-list. What the detail renders and
     * the editor edits; `''` when there is none.
     */
    noteHtml: zod_1.z.string(),
    ownerMemberId: zod_1.z.string(),
    ownerName: zod_1.z.string(),
    /** 21 Oct 2026 · the owner's generated face — the normalised email, via `wireAvatarSeed`. */
    ownerAvatarSeed: zod_1.z.string().nullable(),
    /** 28 Oct 2026 · their uploaded picture's path, when they have one — drawn over the seed's face. */
    ownerAvatarUrl: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    version: zod_1.z.number().int(),
});
/**
 * 31 Oct 2026 · ⚠️ RULE 8 — `GET /invoices/summary`: the list's status tabs, counted on the server
 * now that the list pages. Every filter but the status applies, so a tab's number is what choosing
 * it would list; `total` is the *Összes* tab.
 */
exports.invoiceSummarySchema = zod_1.z.object({
    total: zod_1.z.number().int(),
    byStatus: zod_1.z.object({
        draft: zod_1.z.number().int(),
        issued: zod_1.z.number().int(),
        paid: zod_1.z.number().int(),
        void: zod_1.z.number().int(),
    }),
});
/**
 * ⚠️ **The target arms are a discriminated union**, so a request cannot name a line item and an
 * occurrence at once — the same shape `0134`'s check constraint enforces, expressed where a bad
 * request is refused with a field name rather than a constraint violation.
 */
const targetSchema = zod_1.z.discriminatedUnion('targetKind', [
    zod_1.z.object({ targetKind: zod_1.z.literal('line_item'), lineItemId: zod_1.z.string() }),
    zod_1.z.object({
        targetKind: zod_1.z.literal('occurrence'),
        contractId: zod_1.z.string(),
        occurrenceStartsOn: wireDateSchema,
    }),
]);
exports.saveInvoiceRequestSchema = zod_1.z.intersection(zod_1.z.object({
    externalId: zod_1.z.string().trim().min(1).max(80),
    /** ⚠️ Strictly positive — the table says so too. A zero-forint invoice is a typo. */
    amount: zod_1.z.number().positive(),
    issuedOn: wireDateSchema,
    status: zod_1.z.enum(exports.INVOICE_STATUSES).optional(),
    /**
     * The note as plain text. ⚠️ For a writer that has no markup — the agent, the legacy import:
     * the server derives the HTML from it. Ignored when `noteHtml` is sent.
     */
    note: zod_1.z.string().max(2000).optional(),
    /**
     * ⚠️ `0210` · the note as HTML, sanitised by the server on the comments' allow-list. WINS over
     * `note` when both are sent; the plain twin is derived from it.
     */
    noteHtml: zod_1.z.string().max(20000).optional(),
    ownerMemberId: zod_1.z.string(),
    metafields: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    version: zod_1.z.number().int().optional(),
}), targetSchema);
/**
 * ⚠️ **The status alone, because changing it is what people do most.** The row menu and the badge
 * on the detail both set it without touching an amount, and sending the whole record back to move
 * *Kiállítva* → *Kifizetve* would make a concurrent edit of the note lose.
 */
exports.patchInvoiceStatusRequestSchema = zod_1.z.object({
    status: zod_1.z.enum(exports.INVOICE_STATUSES),
    version: zod_1.z.number().int().optional(),
});
/**
 * What has been billed against a source.
 *
 * ⚠️ **`void` is excluded and nothing else is** — see the header. The amounts are already in the
 * source's currency by the time they reach here; converting is the server's job, because only it
 * holds the rate matrix.
 */
function billedTotal(invoices) {
    return invoices
        .filter((one) => one.status !== 'void')
        .reduce((total, one) => total + Number(one.amount), 0);
}
/**
 * What is left to bill.
 *
 * ⚠️ **Floored at zero, and that is not tidying.** Billing ahead of delivery is ordinary — a
 * retainer invoiced on the first of the month has earned nothing yet — and a negative *Hátralévő*
 * reads as a refund owed rather than as *nothing more to invoice*. The over-billed case is told
 * instead by the progress bar going past 100% in the destructive colour, which is what the mockup
 * draws.
 */
function remainingToBill(revenue, billed) {
    return Math.max(0, revenue - billed);
}
/**
 * How much of the source's revenue has been asked for, as a whole percent — or `null` where there
 * is no revenue to take a share of, which is the same rule `marginPct` follows.
 */
function billedPct(revenue, billed) {
    if (revenue <= 0.004)
        return null;
    return Math.round((billed / revenue) * 100);
}
//# sourceMappingURL=invoice.js.map